
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';
import { tap } from 'rxjs/internal/operators/tap';

import { User } from '../models';
import { UserDisplayNamePipe } from '@app/pipes/userDisplayName/userDisplayName.pipe';

import { AuthService } from './auth.service';
import { SharedUtilsService } from './shared-utils.service';
import { SsModuleService } from './ss-module.service';
import { RoleService } from './role.service';
import { EmailAddressService } from './email-address.service';
import { TeamService } from './team.service';

export interface UserApi {
  items: User[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private currentUserBeingViewed: User = null;

  public passwordPattern = "^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=?!*]).*$";
  public passwordPatternDescription = "Not valid, passwords must be at least 6 characters AND contain at least 1 lowercase, 1 uppercase AND also at least one character from the set @#$%^&+=";

  private API_URL = '/api/users/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  public globalTeam = null;

  // In-memory user cache (replaces Dexie IndexedDB storage).
  private cachedUsers: User[] = [];
  private cacheExpiresAt: number = 0;
  private cacheLifetime = 1000 * 60 * 60 * 4; // 4 hours

  public userTypes = ['Admin', 'Standard', 'Dashboard', 'Provider'];
  public accessTypes = ['Admin', 'Edit', 'View', 'TST', 'TST Admin'];
  public recruiterLevels = ['Junior', 'Associate', 'Senior', 'Executive'];

  public currentSearch = {
    params: null,
    results: null
  };


  constructor(
    private httpClient: HttpClient,
    private formBuilder: UntypedFormBuilder,
    private _sharedUtilsService: SharedUtilsService,
    private _emailAddressService: EmailAddressService,
    private _authService: AuthService,
    private _moduleService: SsModuleService,
    private _roleService: RoleService,
    private _teamService: TeamService
  ) { }

  setCurrentUserBeingViewed(_usr: User): void {
    this.currentUserBeingViewed = _usr;
  }


  getCurrentUserBeingViewed(): User {
    return this.currentUserBeingViewed;
  }


  getCurrentlyLoggedInUser(): User {
    return this._authService.getCurrentlyLoggedInUser();
  }


  getCurrentUsersBaseRoute() {
    const currentUser = this._authService.getCurrentlyLoggedInUser();
    return this.getRouteForUser(currentUser.userType);
  }


  // If not populated it retrieves from db and tries to email then. Handles null values.
  getEmailForUser(_user: string | User): Promise<string | null> {
    return new Promise(async (resolve) => {
      let email_to_return: string | null = null;

      try {
        if (_user != undefined) {
          let userForEmail: User | null;

          if (typeof _user === 'object' && '_id' in _user) {
            userForEmail = _user; // It's already a User.
          } else if (typeof _user === 'string') {
            userForEmail = await this.getUser(_user); // Lookup by ID.
          }

          email_to_return = userForEmail?.email?.length ? userForEmail?.email : null;
        }
      } catch (_err) {
        console.log('There was an issue finding the email for: ', _user);
        console.log('Error: ', _err);
      } finally {
        resolve(email_to_return);
      }
    });
  }


  createUserForProvider(_provider): Promise<User> {
    return new Promise(async (resolve) => {
      // console.log('Creating user from provider: ', _provider);
      const providerEmail = _provider.email;
      // console.log('Provider email: ', providerEmail);

      let savedUser = null;

      // console.log('_provider to create user from: ', _provider);

      if (!providerEmail) {
        console.log('NO EMAIL ADDRESS DETECTED ON PROVIDER!!!');
        resolve(null);
      } else {
        const teams = await this._teamService.getAllTeams(); // Will get all teams. Passing true gets all from api first and will update stored ones.
        const providerTeam = teams.find(_t => _t.name === 'Provider');

        const roles = await lastValueFrom(this._roleService.search({ name: 'Provider' }));
        const providerRole = roles.find(_r => _r.name === 'Provider');

        // console.log('providerTeam: ', providerTeam);
        // console.log('providerRole: ', providerRole);

        const emailForUser = (providerEmail.email_address) ? providerEmail.email_address : providerEmail;

        const providerUser = new User();
        providerUser.email = emailForUser;
        providerUser.active = true;
        providerUser.password = '';
        providerUser.userType = 'Provider';
        providerUser.first_name = _provider.first_name;
        providerUser.last_name = _provider.last_name;
        providerUser.teams = (providerTeam && providerTeam._id) ? [providerTeam._id] : null; // needs to be provider team
        providerUser.roles = (providerRole && providerRole._id) ? [providerRole._id] : null; // [providerRole._id];

        savedUser = await lastValueFrom(this.create(providerUser));
        // await lastValueFrom(this.activate(savedUser));
        await lastValueFrom(this.providerAccountRequest(savedUser));

        resolve(savedUser);
      }
    });
  }


  // Single source of truth for "make sure this provider has a usable portal user."
  // Fixes two long-standing bugs in the hire flow:
  //   1. An EXISTING user matched by email used to be attached silently with no invite sent,
  //      so a provider who never onboarded never received their access email.
  //   2. createUserForProvider() blindly creating on a near-duplicate email hit the unique
  //      index and failed, leaving related_user null and breaking credentialing downstream.
  // Returns the resolved/created user (with _id), or null if there's no email to work with.
  async findOrCreateProviderUser(_provider): Promise<User> {
    const rawEmail = _provider?.email?.email_address ?? _provider?.email ?? null;
    const email = (typeof rawEmail === 'string') ? rawEmail.trim() : null;
    if (!email) return null;

    // check-email is a case-insensitive exact lookup (see server route) so a casing/whitespace
    // difference no longer slips through and creates a duplicate.
    const existingUser = await lastValueFrom(this.checkEmail(email)).catch(() => null);

    if (existingUser?._id) {
      // Reuse the existing account, but make sure they were actually invited. Only (re)send when
      // they've never been invited so we don't reset the password of someone already onboarded.
      if (!existingUser.inviteSent) {
        await lastValueFrom(this.providerAccountRequest(existingUser)).catch(() => null);
      }
      return existingUser;
    }

    // No user yet — create one (this also sends the invite). Guard against the create endpoint
    // returning an error payload instead of a saved user.
    const created = await this.createUserForProvider({ ..._provider, email });
    return (created && created._id) ? created : null;
  }


  updateUserFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<User[]> {
    const updateRequest = { ids: arrayOfIds, fields: fieldsToUpdate };
    return this.httpClient.put<User[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  invalidateUserCache(): void {
    this.cachedUsers = [];
    this.cacheExpiresAt = 0;
  }


  async getAll(_getFreshFromApi: boolean = false, _filterOptions: any = null, _forceRealNameSort: boolean = false): Promise<User[]> {
    try {
      let allUsers: User[] = [];

      // Use in-memory cache if available and not forcing fresh.
      if (!_getFreshFromApi && this.cachedUsers.length && Date.now() < this.cacheExpiresAt) {
        allUsers = this.cachedUsers;
      } else {
        allUsers = await this.fetchUsersFromApi();
      }

      let usersToReturn = (allUsers?.length) ? this._sharedUtilsService.deepClone(allUsers) : []; // Clone so modifications are not picked up on any other call.

      // If params are passed, do any filtering needed here.
      if (_filterOptions) {
        // Normalize true/false comparisons (null/undefined treated as false).
        const trueValues = [true, 'true'];
        const falseValues = [false, 'false', null, undefined];
        let excludedTypesLower: string[] = [];

        // If present build excluded types list once.
        if (_filterOptions.userTypesToExclude?.length) {
          const excludedTypes = Array.isArray(_filterOptions.userTypesToExclude) ? _filterOptions.userTypesToExclude : [_filterOptions.userTypesToExclude];
          excludedTypesLower = excludedTypes.map(_t => _t?.toLowerCase());
        }

        const isTrue = (val: any) => trueValues.includes(val);
        const isFalse = (val: any) => falseValues.includes(val);

        usersToReturn = usersToReturn.filter((_u) => {
          // Active filter.
          if (_filterOptions.hasOwnProperty('active')) {
            if (isTrue(_filterOptions.active) && isFalse(_u?.active)) return false;
            if (isFalse(_filterOptions.active) && !isFalse(_u?.active)) return false;
          }

          // Deleted filter.
          if (_filterOptions.hasOwnProperty('deleted')) {
            if (isTrue(_filterOptions.deleted) && !isTrue(_u?.deleted)) return false;
            if (isFalse(_filterOptions.deleted) && isTrue(_u?.deleted)) return false;
          }

          // Exclude user types filter.
          if (excludedTypesLower.length && excludedTypesLower.includes(_u?.userType?.toLowerCase?.())) {
            return false;
          }

          return true;
        });
      }

      // Sort users by preferred_last_name/last_name A-Z.
      usersToReturn = this.sortUsersByName(usersToReturn, _forceRealNameSort);

      return usersToReturn;
    } catch (_err) {
      console.log('There was an issue getting users from api.', _err?.message);
      return [];
    }
  }


  private async fetchUsersFromApi(): Promise<User[]> {
    const apiUsers = await lastValueFrom(this.httpClient.get<User[]>(this.API_URL)) ?? [];
    this.cachedUsers = apiUsers;
    this.cacheExpiresAt = Date.now() + this.cacheLifetime;
    return apiUsers;
  }


  getAllWithoutStorage(): Observable<User[]> {
    return this.httpClient.get<User[]>(this.API_URL);
  }


  getActiveUsersForDropdown(_skipStorageCheck: boolean = false, _excludeProviderTypes: boolean = false, _forceRealNameSort: boolean = false): Promise<User[]> {
    return new Promise(async (resolve) => {
      try {
        let usersToReturn: User[] = [];

        // Try to load from in-memory cache first if allowed.
        if (!_skipStorageCheck && this.cachedUsers.length && Date.now() < this.cacheExpiresAt) {
          usersToReturn = this.cachedUsers.filter(_u => {
            if (_u?.deleted) return false; // Exclude deleted users.
            if (!_u?.active) return false; // Only include active users.
            if (_excludeProviderTypes && _u?.userType?.toLowerCase?.() === 'provider') return false; // Optionally exclude Providers.

            return true;
          });
        }

        // Fallback: fetch from API if nothing found in cache.
        if (!usersToReturn?.length) {
          const searchTerms = { deleted: false, active: true };
          if (_excludeProviderTypes) searchTerms['userTypesToExclude'] = ['Provider'];
          usersToReturn = await lastValueFrom(this.search(searchTerms));
        }

        // Sort users by preferred_last_name/last_name A-Z.
        usersToReturn = this.sortUsersByName(usersToReturn, _forceRealNameSort);

        const clonedUsers = (usersToReturn?.length) ? this._sharedUtilsService.deepClone(usersToReturn) : []; // Clone so modifications are not picked up on any other call.

        resolve(clonedUsers);
      } catch (_err) {
        resolve([]);
      }
    });
  }


  sortUsersByName(_users: User[], _forceRealName: boolean = false, _byFirstName: boolean = false): User[] {
    if (!_users?.length) return [];

    // Sort users alphabetically by last name (A–Z) by default, or by first name if _byFirstName is true. Uses preferred or real names based on _forceRealName.
    return _users.sort((a, b) => {
      const nameA = this.getSortableName(a, _forceRealName);
      const nameB = this.getSortableName(b, _forceRealName);

      if (_byFirstName) {
        const firstCompare = nameA.first.localeCompare(nameB.first);
        return firstCompare !== 0 ? firstCompare : nameA.last.localeCompare(nameB.last);
      } else {
        const lastCompare = nameA.last.localeCompare(nameB.last);
        return lastCompare !== 0 ? lastCompare : nameA.first.localeCompare(nameB.first);
      }
    });
  }


  getSortableName(_user: User, _forceReal: boolean = false): { first: string, last: string } {
    // Use the pipe here to get name parts to use. This will prevent having to repeat the preferred/normal logic again.
    const _displayNamePipe = new UserDisplayNamePipe();
    const first = _displayNamePipe.transform(_user, { forceReal: _forceReal, format: 'firstOnly', case: 'lower' });
    const last = _displayNamePipe.transform(_user, { forceReal: _forceReal, format: 'lastOnly', case: 'lower' });
    return { first: first, last: last };
  }


  getDisplayName(_user: User, _forceReal: boolean = false, _options?: { format?: string, case?: string }): string {
    if (!_user) return '';

    const _displayNamePipe = new UserDisplayNamePipe();

    // Default to 'firstLast' and 'title' unless overridden
    const format = _options?.format ?? 'firstLast';
    const caseStyle = _options?.case ?? 'title';

    return _displayNamePipe.transform(_user, { forceReal: _forceReal, format: format, case: caseStyle });
  }


  create(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL, JSON.stringify(user), this.headerOptions).pipe(
      tap((createdUser: User) => {
        if (createdUser?._id) this.invalidateUserCache();
      })
    );
  }


  activate(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'activate', JSON.stringify(user), this.headerOptions);
  }


  createTempUserAccount(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'createTempUserAccount', JSON.stringify(user), this.headerOptions);
  }


  checkUserSession(): Observable<any> {
    return this.httpClient.get(this.API_URL + 'user-session');
  }


  updateRoles(_roleUpdateReq): Observable<string[]> {
    return this.httpClient.post<string[]>(this.API_URL + 'update-roles', JSON.stringify(_roleUpdateReq), this.headerOptions);
  }


  updateTeams(_teamUpdateReq): Observable<string[]> {
    return this.httpClient.post<string[]>(this.API_URL + 'update-teams', JSON.stringify(_teamUpdateReq), this.headerOptions);
  }


  register(user: User): Observable<string[]> {
    return this.httpClient.post<string[]>(this.API_URL + 'register', JSON.stringify(user), this.headerOptions);
  }


  providerAccountRequest(user): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'provider-account-invite', JSON.stringify(user), this.headerOptions);
  }


  passwordResetRequest(user): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'password-reset', JSON.stringify(user), this.headerOptions);
  }


  async getUser(id: string, _getFreshFromApi: boolean = false): Promise<User | null> {
    try {
      let foundUser: User | null = null;

      if (id) {
        // Try in-memory cache first.
        if (!_getFreshFromApi && this.cachedUsers.length && Date.now() < this.cacheExpiresAt) {
          foundUser = this.cachedUsers.find(_u => _u._id === id) ?? null;
        }

        // Fetch from API if not found in cache.
        if (!foundUser) {
          foundUser = await lastValueFrom(this.httpClient.get<User>(this.API_URL + id)) ?? null;
        }
      }

      return foundUser;
    } catch (_err) {
      console.log(`There was an issue getting user with ID ${id}:`, _err?.message);
      return null;
    }
  }


  setUserActive(id: string): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + 'mark-user-active/' + id, null, this.headerOptions);
  }


  deactivateUser(id: string): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + 'mark-user-inactive/' + id, null, this.headerOptions);
  }


  delete(id: string): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + 'mark-deleted/' + id, null, this.headerOptions);
  }


  deleteByIdArray(arrayOfIds): Observable<any[]> {
    const deleteRequest = { ids: arrayOfIds };
    return this.httpClient.put<any>(this.API_URL + 'mark-as-deleted/by-array', JSON.stringify(deleteRequest), this.headerOptions);
  }


  checkEmail(email: string): Observable<User> {
    return this.httpClient.get<User>(this.API_URL + 'check-email/' + email);
  }


  updateUser(user: User): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + user._id, JSON.stringify(user), this.headerOptions).pipe(
      tap((updatedUser: User) => {
        if (updatedUser?._id) this.invalidateUserCache();
      })
    );
  }


  completeRegistration(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'completeRegistration', JSON.stringify(user), this.headerOptions);
  }


  tempLogin(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'templogin', JSON.stringify(user), this.headerOptions);
  }


  search(searchTerms): Observable<User[]> {
    return this.httpClient.post<User[]>(this.API_URL + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  updateFieldsById(_id, fieldsToUpdate): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + 'update-certain-fields/' + _id, JSON.stringify(fieldsToUpdate), this.headerOptions).pipe(
      tap((response: any) => {
        // Only clear the cache on a confirmed success so a failed update doesn't wipe good in-memory data.
        if (response?.success) this.invalidateUserCache();

        // Legacy Dexie sync path - keeping commented in case IndexedDB-backed storage is ever restored. Requires reintroducing the Dexie import and initialization elsewhere before this call would do anything useful.
        // (async () => {
        //   try {
        //     const updatedUser = await this.getUser(_id, true); // Force fresh fetch from API and update Dexie.
        //     if (updatedUser) console.log('User refreshed in Dexie after update.');
        //   } catch (err) {
        //     console.error('Failed to sync user storage after field updates:', err);
        //   }
        // })();
      })
    );
  }


  login(user: User) {
    return this.httpClient.post<any>(this.API_URL + 'login', JSON.stringify(user), this.headerOptions);
  }


  logLoginDate(userId: string): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + userId + '/log-login-date/', this.headerOptions);
  }


  getUserRolesAndAccess(_userId: string): Observable<User> {
    return this.httpClient.get<User>(this.API_URL + _userId + '/complete-roles-info');
  }


  logoutWithoutRemovingUser(): Observable<any> {
    return this.httpClient.get(this.API_URL + 'logout', this.headerOptions);
  }


  logout() {
    return this.httpClient.get(this.API_URL + 'logout', this.headerOptions).pipe(
      map((res) => {
        // remove user from local storage to log user out
        localStorage.removeItem('currentUser');
        // res.json();
      })
    );
  }


  getUsersByType(userType: string): Observable<User[]> {
    return this.httpClient.get<User[]>(this.API_URL + 'type/' + userType);
  }


  getUserToAssignTask(): Observable<User[]> {
    return this.httpClient.get<User[]>(this.API_URL + 'user-to-assign-change-requests');
  }


  getUsersForRole(_roleId: string): Observable<User[]> {
    return this.httpClient.get<User[]>(this.API_URL + 'role/' + _roleId);
  }


  updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
    const updateRequest = { ids: arrayOfIds, fields: fieldsToUpdate };
    return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  selectAllSearch(searchTerms): Observable<User[]> {
    searchTerms['selectAll'] = true;
    searchTerms['selectNumberOfRecords'] = false;
    return this.httpClient.post<User[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  selectNumberOfRecordsSearch(searchParams): Observable<any> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = true;
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  getUserCustomDatasource(searchParams, sortField = 'last_name', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<UserApi> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<UserApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  userFormGroup(formData: User) {
    return this.formBuilder.group({
      userId: [formData.userId],
      email: [formData.email, Validators.required],
      password: [formData.password],
      username: [formData.username],
      userType: [formData.userType, Validators.required],
      recruiter_level: [formData.recruiter_level],
      sendgrid_approved: [formData.sendgrid_approved],
      assign_leads: [formData.assign_leads],
      pinning_posts_enabled: [formData.pinning_posts_enabled],
      client: [formData.client],
      first_name: [formData.first_name, Validators.required],
      last_name: [formData.last_name, Validators.required],
      preferred_first_name: [formData.preferred_first_name],
      preferred_last_name: [formData.preferred_last_name],
      phone: [formData.phone],
      phone_ext: [formData.phone_ext],
      other_phone: [formData.other_phone],
      street: [formData.street],
      city: [formData.city],
      state: [formData.state],
      zip: [formData.zip],
      hire_date: [formData.hire_date],
      birth_date: [formData.birth_date],
      active: [formData.active]
    });
  }


  isSuperAdmin(_user) {
    let isSuper = false;

    if (_user.userType === 'System Admin') {
      isSuper = true;
    }

    return isSuper;
  }


  checkForAdminPrivileges(_user) {
    let hasPrivelages = false;

    if (_user.userType === 'System Admin') {
      hasPrivelages = true;
    }

    return hasPrivelages;
  }


  isSystemAdminOrHasCoreProviderAdminRole(_user: User): boolean {
    let hasCoreProviderOrSystemAdmin: boolean = false;

    if (_user?.userType === 'System Admin') {
      hasCoreProviderOrSystemAdmin = true;
    } else if (_user?.roles?.length) {
      hasCoreProviderOrSystemAdmin = _user.roles.some(_role => _role?.name === "Core Provider Calendar Admin") ? true : false;
    }

    return hasCoreProviderOrSystemAdmin;
  }


  getRouteForUser(userType) {
    let userPortal = '/app';

    if (userType === 'System Admin') {
      userPortal = '/admin';
    } else if (userType === 'Provider') {
      userPortal = '/provider';
    }

    return userPortal;
  }


  goToUserProfile(_user) {
    const currentUserLoggedIn = this.getCurrentlyLoggedInUser();
    let builtRoute = '';

    if (_user != undefined && _user._id != undefined) {
      if ((currentUserLoggedIn != undefined && currentUserLoggedIn._id != undefined) && currentUserLoggedIn._id === _user._id) {
        builtRoute = '/app/user/profile';
      } else {
        this.setCurrentUserBeingViewed(_user);
        builtRoute = '/app/user/profile/' + _user._id;
      }

      this._sharedUtilsService.reloadComponentByRoute(builtRoute);
    }
  }


  async getUserTeamByNameVariants(_user): Promise<any> {
    try {
      // Gets team named after the user. Uses both the legal and preferred names.
      let usersTeam: any = null;

      // If passed as an _id, then get the full user.
      if (_user && !_user._id) _user = await this.getUser(_user, true);

      if (_user) {
        // Get both the users legal and preferred name.
        const realUserName: string = this.getDisplayName(_user, true); // Use pipe logic to get real name.
        const preferredUserName: string = this.getDisplayName(_user); // Use pipe logic to get preferred name.

        // Pass both variants to team service to retrieve the matching team.
        usersTeam = await this._teamService.findTeamByNameVariants([realUserName, preferredUserName]);
        // console.log('usersTeam:', usersTeam);
      }

      return usersTeam ?? null;
    } catch (_err) {
      console.log(`There was an issue getting users team:`, _err?.message || _err);
      return null;
    }
  }


  async applyUserNameUpdatesToTeam(_oldRecord: User, _newRecord: User): Promise<User> {
    try {
      if (_oldRecord?._id && _newRecord?._id) {
        const nameFields = ['preferred_first_name', 'preferred_last_name', 'first_name', 'last_name'];

        // Check which fields have changed.
        const changedFields = nameFields.filter(field => {
          const oldValue = (_oldRecord[field] ?? '').trim();
          const newValue = (_newRecord[field] ?? '').trim();
          return oldValue !== newValue;
        });

        // If a field has changed try to update the team name if one is found.
        if (changedFields.length > 0) {
          // console.log('User name fields changed:', changedFields);
          // console.log('Old values:', nameFields.reduce((acc, f) => ({ ...acc, [f]: _oldRecord[f] ?? '' }), {}));
          // console.log('New values:', nameFields.reduce((acc, f) => ({ ...acc, [f]: _newRecord[f] ?? '' }), {}));

          const userTeam = await this.getUserTeamByNameVariants(_oldRecord);
          // console.log('userTeam:', userTeam);

          if (userTeam?._id) {
            const currentPreferredUserName: string = this.getDisplayName(_newRecord); // Use pipe logic to get current preferred name.

            if (currentPreferredUserName !== userTeam?.name) {
              userTeam.name = currentPreferredUserName;
              userTeam.team_name = currentPreferredUserName;
              userTeam.description = `Team for ${currentPreferredUserName}`;

              const updatedTeam = await this._teamService.updateTeam(userTeam);
              // console.log('updatedTeam:', updatedTeam);

              if (updatedTeam?._id) {
                _newRecord.teams = _newRecord.teams || []; // Ensure _newRecord.teams exists

                const currentTeamIndex = _newRecord.teams.findIndex(_t => String(_t?._id) === String(updatedTeam._id));
                if (currentTeamIndex > -1) _newRecord.teams[currentTeamIndex] = updatedTeam;
                else _newRecord.teams.push(updatedTeam);
              }
            }
          }
        } else {
          // console.log('No name fields changed for this user.');
        }
      }

      return _newRecord; // Don't have to use this since it directly updates this record. But return it in case we need it for something.
    } catch (_err) {
      console.log(`There was an issue syncing user changes to team:`, _err?.message || _err);
      return _newRecord ?? null;
    }
  }
}